/**
 * Admin permissions configuration.
 *
 * This file defines which users have admin access and what they can see.
 *
 * Permission types:
 * - overview: Dashboard metrics (attorney usage, export rates, etc.)
 * - billing: Export/import transactions, add matters
 * - entries: View and manage all attorney entries
 * - calendar: View attorney time data in calendar format
 * - support: Support inbox (user feedback)
 *
 * To add a new admin: Add their email (lowercase) with their permissions.
 * To remove admin access: Remove their entry or set all permissions to false.
 */

export type AdminPermissions = {
	overview: boolean;
	billing: boolean;
	entries: boolean;
	calendar: boolean;
	support: boolean;
};

export type AdminPermissionsResult = {
	isAdmin: boolean;
	canView: AdminPermissions;
};

const defaultPermissions: AdminPermissions = {
	overview: false,
	billing: false,
	entries: false,
	calendar: false,
	support: false,
};

const fullPermissions: AdminPermissions = {
	overview: true,
	billing: true,
	entries: true,
	calendar: true,
	support: true,
};

// Local visual-testing override: show the Admin workspace for any signed-in
// user in dev without adding their email to the production allowlist.
const ENABLE_WORKSPACE_PREVIEW = import.meta.env.DEV;

/**
 * Admin configuration by email.
 * Each admin has granular permissions for what they can access.
 */
const adminConfig: Record<string, AdminPermissions> = {
	// ─────────────────────────────────────────────
	// Hourglass Internal - Full access
	// ─────────────────────────────────────────────
	"dayn@hourglass.law": {
		overview: true,
		billing: true,
		entries: true,
		calendar: true,
		support: true,
	},

	"tj@hourglass.law": {
		overview: true,
		billing: true,
		entries: true,
		calendar: true,
		support: true,
	},
	"demo@hourglass.law": {
		overview: true,
		billing: true,
		entries: true,
		calendar: true,
		support: true,
	},

	// ─────────────────────────────────────────────
	// Taylor Day Law
	// ─────────────────────────────────────────────
	"lhb@taylordaylaw.com": {
		overview: true,
		billing: false,
		entries: true,
		calendar: true,
		support: true,
	},
	"rborchers@taylordaylaw.com": {
		overview: true,
		billing: false,
		entries: true,
		calendar: true,
		support: true,
	},

	// ─────────────────────────────────────────────
	// HWS Law Group
	// ─────────────────────────────────────────────
	"bsmith@hwslawgroup.com": {
		overview: false,
		billing: true,
		entries: false,
		calendar: false,
		support: false,
	},
	"wblocker@hwslawgroup.com": {
		overview: true,
		billing: true,
		entries: true,
		calendar: true,
		support: true,
	},

	// ─────────────────────────────────────────────
	// Tyson Mendes
	// ─────────────────────────────────────────────
	"dtyson@tysonmendes.com": {
		overview: true,
		billing: false,
		entries: true,
		calendar: true,
		support: true,
	},
	"clynch@tysonmendes.com": {
		overview: true,
		billing: false,
		entries: true,
		calendar: true,
		support: true,
	},
	"shickey@tysonmendes.com": {
		overview: true,
		billing: false,
		entries: true,
		calendar: true,
		support: true,
	},
	"dreoh@tysonmendes.com": {
		overview: true,
		billing: false,
		entries: true,
		calendar: true,
		support: true,
	},

	// ─────────────────────────────────────────────
	// BPM Law
	// ─────────────────────────────────────────────
	"cdavis@bpmlaw.com": {
		overview: true,
		billing: false,
		entries: true,
		calendar: true,
		support: true,
	},
	"torekhov@bpmlaw.com": {
		overview: true,
		billing: false,
		entries: true,
		calendar: true,
		support: true,
	},

	// ─────────────────────────────────────────────
	// Naman Howell
	// ─────────────────────────────────────────────
	"chipp@namanhowell.com": {
		overview: true,
		billing: false,
		entries: true,
		calendar: true,
		support: true,
	},

	"cstamm@namanhowell.com": {
		overview: true,
		billing: false,
		entries: true,
		calendar: true,
		support: true,
	},

	"ccollins@namanhowell.com": {
		overview: true,
		billing: false,
		entries: true,
		calendar: true,
		support: true,
	},

	// ─────────────────────────────────────────────
	// Gould Ratner
	// ─────────────────────────────────────────────
	"cneukirch@gouldratner.com": {
		overview: true,
		billing: false,
		entries: true,
		calendar: true,
		support: true,
	},
	"jwillis@gouldratner.com": {
		overview: true,
		billing: false,
		entries: true,
		calendar: true,
		support: true,
	},
	"jpisani@gouldratner.com": {
		overview: true,
		billing: false,
		entries: true,
		calendar: true,
		support: true,
	},
	"mhill@gouldratner.com": {
		overview: true,
		billing: false,
		entries: true,
		calendar: true,
		support: true,
	},
	"hourglass@gouldratner.com": {
		overview: true,
		billing: false,
		entries: true,
		calendar: true,
		support: true,
	},

	// ─────────────────────────────────────────────
	// Preg O'Donnell
	// ─────────────────────────────────────────────
	"djardine@pregodonnell.com": {
		overview: true,
		billing: false,
		entries: true,
		calendar: true,
		support: true,
	},
};

/**
 * Get admin permissions for a user by email.
 *
 * @param userEmail - The user's email address
 * @returns Object with isAdmin flag and granular permissions
 *
 * @example
 * const { isAdmin, canView } = getAdminPermissions("dayn@hourglass.law");
 * if (isAdmin && canView.billing) {
 *   // Show billing section
 * }
 */
export const getAdminPermissions = (
	userEmail?: string,
): AdminPermissionsResult => {
	const email = userEmail?.toLowerCase() || "";
	const permissions = adminConfig[email];

	if (!permissions) {
		if (ENABLE_WORKSPACE_PREVIEW && email) {
			return {
				isAdmin: true,
				canView: fullPermissions,
			};
		}

		return {
			isAdmin: false,
			canView: defaultPermissions,
		};
	}

	return {
		isAdmin: true,
		canView: permissions,
	};
};

/**
 * Check if any admin permission is enabled.
 * Useful for determining if someone should see the admin dashboard at all.
 */
export const hasAnyAdminAccess = (userEmail?: string): boolean => {
	const { isAdmin, canView } = getAdminPermissions(userEmail);
	if (!isAdmin) return false;
	return (
		canView.overview ||
		canView.billing ||
		canView.entries ||
		canView.calendar ||
		canView.support
	);
};
